import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import { sessionExpired } from '../../store/authSlice';
import * as auth from '../../services/authClient';

/**
 * 세션이 살아 있는지 **주기적으로** 확인한다 — 화면 없음.
 *
 * 401 을 만나면 알아채는 길(services/sessionExpiry)만으로는 부족했다. QBase 의 목록 조회는 로그인이 풀려도
 * **401 이 아니라 200 에 빈 목록**을 돌려준다(현장 2026-09-23 실측). 그래서 프로젝트·워크플로가 조용히
 * 사라진 것처럼 보이고, 무엇이 잘못됐는지 알 길이 없었다 — 처음 신고된 증상이 이것이다.
 *
 * 그래서 세션 자체를 묻는다. 탭을 다시 볼 때와 몇 분마다. 끊겼으면 다시 로그인 창을 띄운다(SessionExpiredDialog).
 */
const CHECK_MS = 3 * 60 * 1000;

const SessionWatch: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const status = useSelector((s: RootState) => s.auth.status);
  const expired = useSelector((s: RootState) => s.auth.expired);

  useEffect(() => {
    if (status !== 'authenticated' || expired) return undefined;
    let alive = true;
    const check = async () => {
      // 탭이 숨겨져 있으면 묻지 않는다 — 돌아올 때 어차피 확인한다
      if (!alive || document.visibilityState === 'hidden') return;
      try {
        const session = await auth.getSession();
        if (alive && !session) dispatch(sessionExpired());
      } catch { /* 서버에 못 닿은 것은 만료가 아니다 — 다음 차례에 다시 본다 */ }
    };
    const timer = window.setInterval(check, CHECK_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [status, expired, dispatch]);

  return null;
};

export default SessionWatch;
